import { Switch } from '@newfront-insurance/core-ui';
import { ChevronRight } from 'lucide-react';

import {
  getCustomSkillDisplayText,
  getUpdatedLabel,
  hasUnpublishedChanges,
  type CustomSkillManagementTab,
} from './custom-skill-model';
import type { CustomSkillRecord } from '../../../../queries/chats';

export function SettingsPageCustomSkillRow({
  skill,
  listScope,
  isPending,
  isExpanded,
  onOpen,
  onToggle,
  onToggleExpanded,
}: {
  skill: CustomSkillRecord;
  listScope: CustomSkillManagementTab;
  isPending: boolean;
  isExpanded: boolean;
  onOpen: () => void;
  onToggle: () => void;
  onToggleExpanded: () => void;
}) {
  const isSharedList = listScope === 'shared-with-me';
  const displayDescription = getCustomSkillDisplayText(skill.frontmatter.summary, skill.frontmatter.description);

  return (
    <div className="border-b border-[#DEE2E7] bg-white last:border-b-0">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-4 p-4 text-left transition-colors hover:bg-[#FAFBFC]"
        onClick={isSharedList ? onToggleExpanded : onOpen}
        aria-expanded={isSharedList ? isExpanded : undefined}
        aria-label={
          isSharedList && isExpanded
            ? `Collapse details for ${skill.frontmatter.name}`
            : `Open details for ${skill.frontmatter.name}`
        }
      >
        <span className="min-w-0">
          <span className="block truncate font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold text-[#262D46]">
            /{skill.command}
          </span>
          <span className="mt-1 block font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#546A83]">
            {displayDescription}
          </span>
          <span className="mt-2 flex flex-wrap items-center gap-3 font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]">
            <span>
              {isSharedList ? 'Shared with you' : skill.enabled ? 'Enabled' : 'Disabled'}
              {' · '}Updated {getUpdatedLabel(skill.updatedAt)}
            </span>
            {hasUnpublishedChanges(skill) ? (
              <span className="rounded-[6px] bg-[#F4F8FE] px-2 py-1 text-[12px] leading-4 text-[#0957C3]">
                Unpublished changes
              </span>
            ) : null}
          </span>
        </span>
        <ChevronRight
          aria-hidden="true"
          className={`mt-1 h-4 w-4 shrink-0 text-[#98A6B5] transition-transform ${isExpanded ? 'rotate-90' : ''}`}
        />
      </button>

      {isSharedList && isExpanded ? (
        <div className="border-t border-[#DEE2E7] bg-[#FAFBFC] px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[12px] leading-4 text-[#546A83]">{skill.enabled ? 'Enabled' : 'Disabled'}</span>
            <Switch enabled={skill.enabled} onClick={onToggle} locked={isPending} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
